import { Injectable } from '@angular/core';

import { EpAppStore } from 'src/app/shared/services/epAppStoreService/epAppStore.service';
import { EpEnvironmentService } from 'src/app/shared/services/epEnvironmentService/ep-environment.service';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  private permissions: Set<string>;
  private enforcePermissions: boolean;

  constructor(
    private storeService: EpAppStore
  ) {
    const broker = JSON.parse(sessionStorage.getItem('userRole'));
    this.permissions = broker && broker.permissions ? new Set(broker.permissions) : new Set();
    window['permissions'] = window['ENVIRONMENT'].production === 'True' ? { } : this.permissions;
    this.enforcePermissions = EpEnvironmentService.enforcePermissions === true
      || broker.role === 'SystemAdmin' && EpEnvironmentService.enforcePermissionsSysAdmin === true;
  }

  isSystemAdmin(): boolean {
    return (JSON.parse(sessionStorage.getItem('userRole')).role === 'SystemAdmin');
    // affects the pages:
    // all-quotes
  }

  canAltRenewal(): boolean {
    return this.permissions.has('CanAltRenewal');
    // affects the pages:
    // quoting/renew-quote
  }

  canViewRenewalQuote(): boolean {
    return this.permissions.has('CanViewRenewalQuote') || this.permissions.has('CanAltRenewal');
    // affects the pages:
    // renewal/dashboard
    // search > GroupClientTableComponent
  }

  canViewProposals(): boolean {
    // TODO: implemented part of requirenments. "We might not have to do it for mvp"
    return this.permissions.has('CanViewProposals');
    // affects the pages:
    // renewal/dashboard
  }

  canViewFullSSN(): boolean {
    return this.permissions.has('CanViewFullSSN');
    // affects the pages:
    // quoting/enrollment
  }

  canClone(): boolean {
    return this.permissions.has('CanClone');
    // affects the pages:
    // new-sales-dashboard
    // renewal/dashboard
  }

  canCreateGroup(): boolean {
    return this.permissions.has('CanCreateGroup');
    // affects the pages:
    // new-sales-dashboard
    // search
  }

  canCreateQuote(): boolean {
    return this.permissions.has('CanCreateQuote');
    // affects the pages:
    // all-quotes
  }

  canDeclineSubmit(): boolean {
    return this.permissions.has('CanDeclineSubmit');
    // affects the pages:
    // renewal/dashboard
  }

  canDeclineReview(): boolean {
    return this.permissions.has('CanDeclineReview');
    // affects the pages:
    // renewal/dashboard
  }

  canEditDental(): boolean {
    // This is for renewals only.  This value can be changed at any time for a new sales quote.
    const { isRenewalQuote } = this.storeService.select('currentQuote').value;

    return !isRenewalQuote || this.permissions.has('CanEditDental');
    // affects the pages:
    // quoting/plan-select
  }

  canOverrideEligibility(): boolean {
    return this.permissions.has('CanOverrideEligibility');
    // affects the pages:
    // renewal/dashboard
  }

  canRenewAsIs(): boolean {
    return this.permissions.has('CanRenewAsIs');
    // affects the pages:
    // renewal/dashboard
  }

  canResubmit(): boolean {
    return this.enforcePermissions && this.permissions.has('CanResubmit');
    // affects the pages:
    // new-sales-dashboard
    // renewal/dashboard
  }

  canReviewCompliance(): boolean {
    return this.permissions.has('CanReviewCompliance');
    // affects the pages:
    // quoting/compliance-review
  }

  canSearchGroups(): boolean {
    return this.permissions.has('CanSearchGroups');
    // affects:
    // HeaderComponent
  }

  canViewGeneralAgency(): boolean {
    return this.permissions.has('CanViewGeneralAgency');
    // affects:
    // renewal/dashboard
  }

  canViewFailed(): boolean {
    return this.permissions.has('CanViewFailed');
    // affects:
    // renewal/dashboard
    // new-sales-dashboard
  }

  canViewError(): boolean {
    return this.enforcePermissions && this.permissions.has('CanViewError');
    // affects:
    // renewal/dashboard
    // new-sales-dashboard
  }

  canCreateCombo(): boolean {
    return this.enforcePermissions && this.permissions.has('CanCreateCombo');
    // affects:
    // renewal/dashboard
    // new-sales-dashboard
  }

  canViewGroupDetails(): boolean {
    return this.permissions.has('CanViewGroupDetails') ||
      this.permissions.has('CanEditGroupDetails') ||
      this.permissions.has('CanEditCorpID');
    // affects:
    // renewal/dashboard
    // new-sales-dashboard
    // all-quotes
  }

  canEditGroupDetails(): boolean {
    // The user can edit and save group demographics except CorpID.
    return this.permissions.has('CanEditGroupDetails');
    // affects the pages:
    // group-details
  }

  canEditCorpID(): boolean {
    return this.permissions.has('CanEditCorpID');
    // affects:
  }

  сanTenDays(): boolean {
    return this.permissions.has('CanTenDays');
    // affects:
  }

  canViewPlanDetails(): boolean {
    return this.permissions.has('CanViewPlanDetails');
    // affects:
    // subgroup-details
  }

  canViewGUID(): boolean {
    return this.permissions.has('CanViewGUID');
    // affects:
    // subgroup-details
  }

  canViewBrokerSpecific(): boolean {
    return this.permissions.has('CanViewBrokerSpecific');
    // affects:
  }

  canViewAllTermReasons(): boolean {
    return this.permissions.has('CanViewAllTermReasons');
    // affects:
    // EpQuoteDeclinationPopupComponent
  }

  canViewVisionCareEnhanced(): boolean {
    return this.permissions.has('CanViewVisionCareEnhanced');
    // affects:
  }

  canViewQuotes(): boolean {
    return this.permissions.has('CanViewQuotes')
      || this.canViewQuote();
    // affects:
    // search
  }

  canViewQuote(): boolean {
    // NOT FOR MVP. Added for using Inherited Permissions
    return this.permissions.has('CanViewQuote')
      || this.canEditQuote();
    // affects:
    // new-sales-dashboard
    // all-quotes
  }

  canEditQuote(): boolean {
    return this.permissions.has('CanEditQuote');
    // affects:
    // new-sales-dashboard
    // all-quotes
  }

  canQuoteACA(): boolean {
    return this.permissions.has('CanQuoteACA');
    // affects:
    // search
  }

  сanAddtoCart(): boolean {
    return this.permissions.has('CanAddToCart');
  }

  canViewRenewalConfirmation(): boolean {
    return this.permissions.has('CanViewRenewalConfirmation');
  }

  canViewCalc(): boolean {
    return this.permissions.has('CanViewCalc');
  }

  canViewAncillaryManager(): boolean {
    return this.enforcePermissions;
  }

  canCreateProposal(): boolean {
    return true; // there is currently no need to configure this permission using this.enforcePermissions
  }

  canViewAddToCart(): boolean {
    return true;  // there is currently no need to configure this permission using this.enforcePermissions
  }

  canComparePlans(): boolean {
    return this.permissions.has('CanComparePlans');
  }

  canSubmitEnrollment(): boolean {
    // return (this.enforcePermissions || EpEnvironmentService.enforcePermissionsSysAdmin === 'true')
    //   && this.permissions.has('CanSubmitEnrollment');
    return this.permissions.has('CanSubmitEnrollment');
  }

  canEnroll(): boolean {
    return this.permissions.has('CanEnroll');
  }

  // canAcceptEnrollment(): boolean {
  //   return (this.enforcePermissions || EpEnvironmentService.enforcePermissionsSysAdmin === 'true');
  // }

  canAcceptEnrollmentNS(): boolean {
    return this.permissions.has('CanAcceptEnrollmentNS');
  }

  canViewVisionAdvantage(): boolean {
    return this.permissions.has('CanViewVisionAdvantage');
  }

  canEditSubgroupNum(): boolean {
    return this.permissions.has('CanEditSubgroupNum');
  }
}
